import React from "react";

// External Components
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

// Icons
import closeIcon from "../../../../assets/icons/close.svg";
import infoIcon from "../../../../assets/icons/info-circle.svg";

// Custom Components
import ExcelUploadButton from "../../buttons/ExcelUploadButton/ExcelUploadButton";

// Utilities
import { notify } from "../../../Notify/Notify";
import { v4 } from "uuid";

// Stylesheets
import "./ExcelUploadPopup.scss";
import { isEmailValid } from "../../../../helper";

export default function ExcelUploadPopup({ open, handleClose }) {
  const handleFileChange = async (file) => {
    if (!file) {
      handleClose();
      return;
    }

    try {
      const XLSX = await import("xlsx");
      const data = await parseFile(file, XLSX);
      handleClose(true, data);
    } catch (error) {
      console.error("Error parsing file:", error.message);

      const title = "Error";
      let detail = "Something went wrong.";

      const specificErrors = [
        "Excel file does not contain any sheets.",
        "Excel file format is invalid. Both 'Email' and 'Name' columns are required.",
        "Excel file is empty.",
      ];

      if (specificErrors.includes(error.message)) {
        detail = error.message;
      }

      const type = "error";
      notify(title, detail, type);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="excel-upload-popup"
      aria-describedby="excel-upload-popup-desc"
      className="new-design-v1 excel-upload-popup"
    >
      <DialogTitle id="excel-upload-popup-title">
        <div className="excel-upload-popup-header">
          <div className="excel-upload-popup-header-label">Upload Excel</div>
          <div>
            <img
              src={closeIcon}
              alt="close"
              className="close"
              onClick={() => handleClose()}
            />
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="excel-upload-popup-content">
          <div className="excel-upload-popup-content-heading">
            <div>
              <img src={infoIcon} alt="info" />
            </div>
            <div className="excel-upload-popup-content-heading-label">
              Attention
            </div>
          </div>
          <div className="excel-upload-popup-content-description">
            {"Uploading an excel will overwrite the existing mailing list."}
          </div>
        </div>
      </DialogContent>
      <DialogActions className="excel-upload-popup-footer">
        <button
          className="secondary-button mr-4"
          style={{ maxWidth: "79px" }}
          onClick={() => handleClose()}
        >
          Cancel
        </button>
        <ExcelUploadButton
          onChange={(file) => {
            handleFileChange(file);
          }}
        />
      </DialogActions>
    </Dialog>
  );
}

/**
 * Parses an Excel file and extracts data based on specified columns.
 *
 * @param {File} file - The Excel file to parse.
 * @param {Object} XLSX - The XLSX library object.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of objects containing parsed data.
 * @throws {Error} Throws an error if the Excel file format is invalid, empty, or does not contain any sheets.
 */
const parseFile = (file, XLSX) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });

        // Ensure the workbook has at least one sheet
        if (workbook.SheetNames.length === 0) {
          reject(new Error("Excel file does not contain any sheets."));
          return;
        }

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Find the columns with headers "Email" and "Name"
        const headerRow = findHeaderRow(sheet, XLSX);
        const emailColumnIndex = findColumnIndex(headerRow, "Email");
        const nameColumnIndex = findColumnIndex(headerRow, "Name");

        // Ensure both "Email" and "Name" columns are present
        if (emailColumnIndex === -1 || nameColumnIndex === -1) {
          reject(
            new Error(
              "Excel file format is invalid. Both 'Email' and 'Name' columns are required."
            )
          );
          return;
        }

        // Extract data skipping the first row as header
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1, range: 1 });

        // Ensure the sheet is not empty
        if (data.length === 0) {
          reject(new Error("Excel file is empty."));
          return;
        }

        const parsedData = data.map((row) => {
          return {
            id: v4(),
            email: row[emailColumnIndex],
            name: row[nameColumnIndex],
          };
        });
        const result = parsedData?.filter((user) =>
          isValidRow(user?.email, user?.name)
        );
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.readAsBinaryString(file);
  });
};
/**
 * Finds the header row of an Excel sheet.
 *
 * @param {Object} sheet - The Excel sheet object.
 * @param {Object} XLSX - The XLSX library object.
 * @returns {Array} An array containing the values of the header row.
 */
const findHeaderRow = (sheet, XLSX) => {
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const headerRow = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cellAddress = { r: range.s.r, c: C };
    const cellRef = XLSX.utils.encode_cell(cellAddress);
    headerRow.push(sheet[cellRef].v);
  }
  return headerRow;
};

/**
 * Finds the index of a column based on its header.
 *
 * @param {Array} headerRow - An array containing the values of the header row.
 * @param {string} header - The header to search for.
 * @returns {number} The index of the column, or -1 if not found.
 */
const findColumnIndex = (headerRow, header) => {
  const lowerCaseHeader = header.toLowerCase();
  for (let i = 0; i < headerRow.length; i++) {
    if (headerRow[i].toLowerCase() === lowerCaseHeader) {
      return i;
    }
  }
  return -1;
};

function isValidRow(email, name) {
  // Trim whitespace and check if the length is greater than one for name
  if (!name) {
    return false;
  }
  const isNameValid = name?.trim().length > 1;

  // Return true only if both name and email are valid
  return isNameValid && isEmailValid(email);
}
